/**
 * RoamerControlScreen.tsx — ONBOARDING STEP 3 OF 3
 * ROAMER CONTROL™ (Build 2.00.0)
 *
 * Shown as a full-screen portal overlay AFTER the wizard completes,
 * while Home loads silently underneath. The "airport gate" moment —
 * security is cleared, the OS is live, the operator steps into command.
 *
 * Architecture:
 *   • Portal overlay (renders over Home, which is already mounted below)
 *   • Reads persona data passed from Shell (already saved by wizard)
 *   • On CTA press: plays 6-step launch sequence, then fades out → onComplete()
 *   • No game logic, no forms, no settings — pure welcome moment
 *   • All animations: transform + opacity only (GPU-friendly)
 *   • AUTH FREEZE: this component has no auth logic whatsoever
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { OperatorPersona } from '@/types/operatorPersona';

// ── Design tokens (mirrors OperatorCreationWizard) ─────────────────────────
const T = {
  bg:           '#030d1f',
  surface:      'rgba(6,20,50,0.96)',
  card:         'rgba(255,255,255,0.04)',
  cardBorder:   'rgba(255,255,255,0.08)',
  glass:        'rgba(255,255,255,0.03)',
  glassBorder:  'rgba(255,255,255,0.07)',
  inputBorder:  'rgba(255,255,255,0.12)',
  accent:       '#3b82f6',
  accentDim:    'rgba(59,130,246,0.14)',
  accentBorder: 'rgba(59,130,246,0.38)',
  teal:         '#0dd3c5',
  tealDim:      'rgba(13,211,197,0.10)',
  success:      '#22c55e',
  successDim:   'rgba(34,197,94,0.12)',
  successBorder:'rgba(34,197,94,0.35)',
  text:         '#f1f5f9',
  muted:        'rgba(255,255,255,0.46)',
  dim:          'rgba(255,255,255,0.24)',
  mono:         'monospace' as const,
} as const;

// ── CSS ────────────────────────────────────────────────────────────────────
const RC_CSS = `
  @keyframes rc-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes rc-fade-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes rc-scale-in {
    from { opacity: 0; transform: scale(0.93); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes rc-scan-in {
    from { opacity: 0; transform: translateX(-8px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes rc-badge-in {
    from { opacity: 0; transform: scale(0.82); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes rc-status-pulse {
    0%, 100% { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
    50%       { box-shadow: 0 0 0 14px rgba(34,197,94,0); }
  }
  @keyframes rc-glow-pulse {
    0%, 100% { box-shadow: 0 0 28px rgba(59,130,246,0.28), 0 0 0 1px rgba(59,130,246,0.22); }
    50%       { box-shadow: 0 0 52px rgba(59,130,246,0.52), 0 0 0 1px rgba(59,130,246,0.40); }
  }
  @keyframes rc-breathe {
    0%, 100% { opacity: 0.72; }
    50%       { opacity: 1; }
  }
  @keyframes rc-screen-out {
    0%   { opacity: 1; }
    100% { opacity: 0; }
  }
  @keyframes rc-counter-tick {
    from { transform: scale(1.4); opacity: 0.6; }
    to   { transform: scale(1);   opacity: 1; }
  }
  @keyframes rc-btn-connecting {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.55; }
  }

  .rc-overlay  { animation: rc-fade-in 0.32s ease both; }
  .rc-card     { animation: rc-scale-in 0.28s ease both; }
  .rc-fade-up  { animation: rc-fade-up 0.36s ease both; }
  .rc-scan     { animation: rc-scan-in 0.22s ease both; }
  .rc-badge    { animation: rc-badge-in 0.26s cubic-bezier(0.34,1.56,0.64,1) both; }
  .rc-status-orb { animation: rc-status-pulse 2.2s ease-in-out infinite; }
  .rc-glow     { animation: rc-glow-pulse 2.6s ease-in-out infinite; }
  .rc-breathe  { animation: rc-breathe 3.4s ease-in-out infinite; }
  .rc-screen-out { animation: rc-screen-out 0.8s ease forwards; }
  .rc-connecting { animation: rc-btn-connecting 1.1s ease-in-out infinite; }
`;

// ── Data helpers ───────────────────────────────────────────────────────────

const COUNTRY_CURRENCY: Record<string, string> = {
  US: 'USD', GB: 'GBP', EU: 'EUR', DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR',
  PT: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', FI: 'EUR', GR: 'EUR', IE: 'EUR',
  JP: 'JPY', KR: 'KRW', CN: 'CNY', IN: 'INR', AU: 'AUD', CA: 'CAD',
  NZ: 'NZD', CH: 'CHF', SG: 'SGD', HK: 'HKD', TH: 'THB', ID: 'IDR',
  MY: 'MYR', PH: 'PHP', VN: 'VND', TW: 'TWD', MX: 'MXN', BR: 'BRL',
  AR: 'ARS', CO: 'COP', CL: 'CLP', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  RO: 'RON', BG: 'BGN', HR: 'HRK', RS: 'RSD', UA: 'UAH', TR: 'TRY',
  AE: 'AED', SA: 'SAR', EG: 'EGP', ZA: 'ZAR', NG: 'NGN', KE: 'KES',
  GH: 'GHS', MA: 'MAD', TN: 'TND', IL: 'ILS', IQ: 'IQD', PK: 'PKR',
  BD: 'BDT', LK: 'LKR', NP: 'NPR', MM: 'MMK', KH: 'KHR', LA: 'LAK',
};

const COUNTRY_LANGUAGE: Record<string, string> = {
  US: 'English', GB: 'English', AU: 'English', CA: 'English', NZ: 'English',
  IE: 'English', SG: 'English', PH: 'English', IN: 'English/Hindi',
  DE: 'German', AT: 'German', CH: 'German/French', FR: 'French', BE: 'French/Dutch',
  ES: 'Spanish', MX: 'Spanish', AR: 'Spanish', CO: 'Spanish', CL: 'Spanish',
  PT: 'Portuguese', BR: 'Portuguese', IT: 'Italian', NL: 'Dutch',
  PL: 'Polish', CZ: 'Czech', HU: 'Hungarian', RO: 'Romanian',
  JP: 'Japanese', KR: 'Korean', CN: 'Mandarin', TW: 'Traditional Chinese',
  TH: 'Thai', VN: 'Vietnamese', ID: 'Indonesian', MY: 'Malay',
  TR: 'Turkish', AE: 'Arabic', SA: 'Arabic', EG: 'Arabic', IL: 'Hebrew',
  ZA: 'English/Zulu', NG: 'English', KE: 'English/Swahili',
};

const QUOTES = [
  '"The world becomes your office."',
  '"Go where opportunity takes you."',
  '"Every journey begins with a single mission."',
  '"Your base is wherever your work thrives."',
  '"The modern roamer doesn\'t find work. Work finds them."',
];

const BOOT_SYSTEMS = [
  'Location Services Online',
  'Roamer Intelligence Online',
  'Learning Engine Online',
  'Career Engine Online',
  'Planner Online',
  'Health Online',
  'Marketplace Online',
  'Community Online',
  'Wallet Online',
  'Passport Online',
];

const LAUNCH_LINES = [
  'Initializing Roamer Control...',
  'Loading operator profile...',
  'Synchronizing mission...',
  'Connecting intelligence engines...',
  'Preparing workspace...',
  'System online.',
];

const CLEARANCE_ITEMS = [
  'Identity Verified',
  'Security Cleared',
  'Travel Essentials Packed',
  'Operator Authorized',
];

// ── Props ──────────────────────────────────────────────────────────────────
interface Props {
  persona: OperatorPersona | null;
  onComplete: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────
export default function RoamerControlScreen({ persona, onComplete }: Props) {
  // Boot sequence state — each system reveals at 280ms intervals
  const [bootCount, setBootCount] = useState(0);
  // Launch sequence state
  const [launching, setLaunching] = useState(false);
  const [launchLine, setLaunchLine] = useState(0);
  const [exiting, setExiting] = useState(false);
  const completedRef = useRef(false);

  const quote = QUOTES[Math.floor(Math.random() * QUOTES.length)];

  // ── Auto boot sequence on mount ──────────────────────────────────────────
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    BOOT_SYSTEMS.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setBootCount(c => Math.max(c, i + 1));
      }, 400 + i * 280));
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  // ── Launch sequence ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!launching) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    LAUNCH_LINES.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setLaunchLine(i + 1);
      }, i * 620));
    });
    // Fade out after last line
    timers.push(setTimeout(() => {
      setExiting(true);
    }, LAUNCH_LINES.length * 620 + 400));
    // Call onComplete after fade
    timers.push(setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, LAUNCH_LINES.length * 620 + 1280));
    return () => timers.forEach(clearTimeout);
  }, [launching, onComplete]);

  // ── Derived data ─────────────────────────────────────────────────────────
  const name = persona?.displayName?.split(/\s+/)[0] || 'Roamer';
  const currentBase = persona?.currentBase;
  const homeBase = persona?.homeBase;
  const timezone = persona?.timezone || currentBase?.timezone || '—';
  const cc = (currentBase?.countryCode || '').toUpperCase();
  const currency = COUNTRY_CURRENCY[cc] || '—';
  const language = COUNTRY_LANGUAGE[cc] || '—';
  const monthlyGoal = persona?.incomeGoalMonthly || 0;

  // ── Layout ───────────────────────────────────────────────────────────────
  const body = (
    <>
      <style>{RC_CSS}</style>
      <div
        className={`rc-overlay${exiting ? ' rc-screen-out' : ''}`}
        style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: T.bg,
          overflowY: 'auto', overflowX: 'hidden',
          display: 'flex', flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: 'rgba(3,13,31,0.97)', backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '14px 20px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* Top row: step counter + title */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.20em', color: T.muted }}>
              STEP 3 OF 3
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 13, color: T.accent }}>⊕</span>
              <span style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 900, letterSpacing: '0.22em', color: T.text }}>
                ROAMER CONTROL™
              </span>
              <span style={{ fontSize: 13, color: T.accent }}>⊕</span>
            </div>
            <div style={{ width: 56 }} />{/* spacer to center the title */}
          </div>

          {/* Progress bar — 100% filled */}
          <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: '100%', borderRadius: 2,
              background: `linear-gradient(90deg, ${T.accent}, ${T.teal})`,
              boxShadow: `0 0 10px rgba(59,130,246,0.55)`,
            }} />
          </div>
        </div>

        {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '28px 20px',
          paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
          gap: 20, maxWidth: 480, margin: '0 auto', width: '100%',
        }}>

          {/* ── Headline ──────────────────────────────────────────────────── */}
          <div className="rc-fade-up" style={{ textAlign: 'center', animationDelay: '0.08s' }}>
            <div style={{
              fontFamily: T.mono, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.28em', color: 'rgba(96,165,250,0.65)',
              marginBottom: 10,
            }}>
              WELCOME TO
            </div>
            <h1 style={{
              fontFamily: T.mono, fontSize: 24, fontWeight: 900,
              letterSpacing: '0.08em', color: T.text, margin: 0,
              lineHeight: 1.25,
            }}>
              ROAMER CONTROL™
            </h1>
            <div style={{
              fontFamily: T.mono, fontSize: 12, color: T.muted,
              marginTop: 10, letterSpacing: '0.04em',
            }}>
              Your personal operating system is ready.
            </div>
          </div>

          {/* ── Clearance status ──────────────────────────────────────────── */}
          <div
            className="rc-card"
            style={{
              width: '100%',
              background: T.glass, border: `1px solid ${T.glassBorder}`,
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', flexDirection: 'column', gap: 11,
              animationDelay: '0.16s',
            }}
          >
            <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.28em', color: T.dim, marginBottom: 4 }}>
              CLEARANCE STATUS
            </div>
            {CLEARANCE_ITEMS.map((item, i) => (
              <div
                key={item}
                className="rc-scan"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  animationDelay: `${0.22 + i * 0.09}s`,
                }}
              >
                <div className="rc-badge" style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  background: T.successDim, border: `1.5px solid ${T.successBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animationDelay: `${0.26 + i * 0.09}s`,
                }}>
                  <span style={{ fontSize: 10, color: T.success, fontWeight: 900, lineHeight: 1 }}>✓</span>
                </div>
                <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: '0.05em' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* ── ROAMER STATUS indicator ───────────────────────────────────── */}
          <div
            className="rc-fade-up"
            style={{
              width: '100%',
              background: T.successDim, border: `1px solid ${T.successBorder}`,
              borderRadius: 14, padding: '16px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              animationDelay: '0.54s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="rc-status-orb" style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                background: T.success,
              }} />
              <div>
                <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.26em', color: 'rgba(74,222,128,0.70)', marginBottom: 3 }}>
                  ROAMER STATUS
                </div>
                <div style={{ fontFamily: T.mono, fontSize: 15, fontWeight: 900, color: T.success, letterSpacing: '0.10em' }}>
                  MISSION READY
                </div>
              </div>
            </div>
            <div style={{ fontFamily: T.mono, fontSize: 26, color: T.success, lineHeight: 1 }}>🟢</div>
          </div>

          {/* ── Mission brief ─────────────────────────────────────────────── */}
          <div
            className="rc-card"
            style={{
              width: '100%',
              background: T.card, border: `1px solid ${T.cardBorder}`,
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', flexDirection: 'column', gap: 0,
              animationDelay: '0.62s',
            }}
          >
            <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.28em', color: T.dim, marginBottom: 14 }}>
              MISSION BRIEF — {name.toUpperCase()}
            </div>
            {[
              { label: 'CURRENT BASE', value: currentBase ? `${currentBase.city}, ${currentBase.country}` : '—' },
              homeBase && homeBase.city !== currentBase?.city
                ? { label: 'HOME BASE', value: `${homeBase.city}, ${homeBase.country}` }
                : null,
              { label: 'MISSION', value: monthlyGoal > 0 ? `$${monthlyGoal.toLocaleString()}/month` : '—' },
              { label: 'CURRENCY', value: currency },
              { label: 'TIME ZONE', value: timezone },
              { label: 'WEATHER', value: 'Detecting...' },
              { label: 'LANGUAGE', value: language },
            ].filter(Boolean).map((row, i) => (
              <div
                key={row!.label}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  padding: '7px 0',
                  borderBottom: i < 6 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}
              >
                <div style={{ fontFamily: T.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.18em', color: T.dim, width: 90, flexShrink: 0 }}>
                  {row!.label}
                </div>
                <div style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 600, color: row!.value === 'Detecting...' ? T.muted : T.text }}>
                  {row!.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── Roamer quote ──────────────────────────────────────────────── */}
          <div
            className="rc-breathe rc-fade-up"
            style={{
              width: '100%', textAlign: 'center',
              fontFamily: T.mono, fontSize: 13, fontStyle: 'italic',
              color: T.muted, lineHeight: 1.65, letterSpacing: '0.02em',
              padding: '4px 12px',
              animationDelay: '0.70s',
            }}
          >
            {quote}
          </div>

          {/* ── System startup ────────────────────────────────────────────── */}
          <div
            className="rc-card"
            style={{
              width: '100%',
              background: T.card, border: `1px solid ${T.cardBorder}`,
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', flexDirection: 'column', gap: 10,
              animationDelay: '0.74s',
            }}
          >
            <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.28em', color: T.dim, marginBottom: 4 }}>
              SYSTEM STARTUP
            </div>
            {BOOT_SYSTEMS.map((sys, i) => {
              const online = i < bootCount;
              return (
                <div
                  key={sys}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11,
                    opacity: online ? 1 : 0.22,
                    transition: 'opacity 0.28s ease',
                  }}
                >
                  <span style={{
                    fontSize: 11,
                    color: online ? T.success : T.dim,
                    transition: 'color 0.28s ease',
                    lineHeight: 1,
                  }}>
                    {online ? '✓' : '○'}
                  </span>
                  <span style={{
                    fontFamily: T.mono, fontSize: 11, fontWeight: online ? 700 : 400,
                    color: online ? T.text : T.muted,
                    letterSpacing: '0.05em',
                    transition: 'color 0.28s, font-weight 0.28s',
                  }}>
                    {sys}
                  </span>
                </div>
              );
            })}
          </div>

        </div>{/* end main content */}

        {/* ── STICKY FOOTER — CTA ─────────────────────────────────────────── */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 11,
          padding: '14px 20px',
          paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(3,13,31,0.97)', backdropFilter: 'blur(14px)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          {/* Launch sequence lines */}
          {launching && (
            <div style={{
              marginBottom: 12,
              display: 'flex', flexDirection: 'column', gap: 5,
            }}>
              {LAUNCH_LINES.slice(0, launchLine).map((line, i) => (
                <div
                  key={line}
                  className="rc-scan"
                  style={{ animationDelay: '0s' }}
                >
                  <span style={{
                    fontFamily: T.mono, fontSize: 10.5, fontWeight: 700,
                    color: i === launchLine - 1
                      ? (line === 'System online.' ? T.success : T.teal)
                      : T.dim,
                    letterSpacing: '0.08em',
                  }}>
                    {i < launchLine - 1 ? '✓ ' : '▸ '}{line}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* CTA button */}
          <button
            disabled={launching}
            onClick={() => setLaunching(true)}
            className={launching ? 'rc-connecting' : 'rc-glow'}
            style={{
              width: '100%', height: 52,
              background: launching
                ? 'rgba(59,130,246,0.22)'
                : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 40%, #0369a1 100%)',
              border: `1px solid ${launching ? T.accentBorder : 'rgba(59,130,246,0.55)'}`,
              borderRadius: 12, cursor: launching ? 'default' : 'pointer',
              fontFamily: T.mono, fontSize: 13, fontWeight: 900,
              letterSpacing: '0.18em', color: '#ffffff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'background 0.25s, border-color 0.25s',
            }}
          >
            {launching ? 'CONNECTING...' : 'ENTER ROAMER CONTROL →'}
          </button>
        </div>
      </div>
    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}
